//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import Avatar from '@components/atoms/avatar/Avatar';
import SourceActionButton from '@components/atoms/source/SourceActionButton';
import Text from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import IItemMetadata, {
  IItemMetadataSocialDetails,
} from '@models/IItemMetadata';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalItemType } from '@models/UniversalItemType';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

export type ItemMetadata = Pick<IItemMetadata, 'originalUrl' | 'socialNetwork'>;

type UserMetadata = Pick<
  IItemMetadataSocialDetails,
  | 'networkUserId'
  | 'networkUserDisplayName'
  | 'networkUsername'
  | 'networkUserImageUrl'
>;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  itemInfo?: ItemMetadata;
  userInfo: UserMetadata;
  missingUsernameHelpText?: string;
  universalItemType?: UniversalItemType;
  size?: number;
  ingestionSource?: IContentLibraryItem['ingestionSource'];
  isYoutubeCreatorTrackingEnabled?: boolean; // TODO: PD-255285 Remove this prop feature flag once released
}

/**
 * The Source atom renders user and source metadata
 * for an item. For social items, this includes an
 * avatar, username, first and last name, and a
 * button that indicates the item's network. If possible,
 * we'll open the original posts URL in a new tab when
 * the user clicks said button.
 *
 * When an item does not come from a network (e.g. it's
 * an upload), we'll display whatever information we have
 * and use a fallback avatar.
 *
 * When a username is not present, we'll display "No Data",
 * providing a help tooltip if help text is provided.
 */
const Source = ({
  className,
  ingestionSource,
  isYoutubeCreatorTrackingEnabled = false,
  itemInfo,
  missingUsernameHelpText,
  size,
  universalItemType,
  userInfo: { networkUserDisplayName, networkUserImageUrl, networkUsername },
}: Props) => {
  const networkUserInitials = /(.)?[a-zA-Z]* ?(.)?/
    .exec(networkUserDisplayName || '')
    ?.slice(1, 3)
    .join('');

  const isClickable =
    !!networkUsername &&
    (itemInfo?.socialNetwork === SocialNetwork.Instagram ||
      itemInfo?.socialNetwork === SocialNetwork.TikTok ||
      (isYoutubeCreatorTrackingEnabled &&
        itemInfo?.socialNetwork === SocialNetwork.YouTube));

  const isProduct =
    universalItemType && universalItemType === UniversalItemType.Product;

  const isUploaded =
    universalItemType && universalItemType === UniversalItemType.MediaImport;

  const userLink = isClickable
    ? `/influencers/user/${networkUsername}${isDefined(itemInfo.socialNetwork) ? `?network=${itemInfo.socialNetwork}` : ''}`
    : null;

  const usernameToRender = (() => {
    if (networkUsername) {
      return networkUsername;
    } else if (isUploaded) {
      if (ingestionSource === 'Dropbox') {
        return 'Dropbox';
      }
      return 'Uploaded';
    } else {
      return null;
    }
  })();

  const usernameField = usernameToRender ? (
    <Text ellipsize={1} height={16} mixin={Mixins.Text.BodyBold}>
      {usernameToRender}
    </Text>
  ) : (
    <SmallTooltip
      className={className}
      content={missingUsernameHelpText}
      isDisabled={false}
      placement='bottom'
      target={
        <MissingUsername>
          <MissingUsernameText
            ellipsize={1}
            height={16}
            mixin={Mixins.Text.BodySecondary}
          >
            No Data
          </MissingUsernameText>
        </MissingUsername>
      }
    />
  );

  const displayNameField = networkUserDisplayName &&
    (networkUsername || isUploaded) && (
      <Text ellipsize={1} height={16} mixin={Mixins.Text.BodySecondary}>
        {networkUserDisplayName}
      </Text>
    );

  const userContainer = isProduct ? (
    <SmallTooltip
      content={
        <UserContainer>
          <Text mixin={Mixins.Text.BodyBold}>{usernameToRender}</Text>
          {displayNameField}
        </UserContainer>
      }
      isDisabled={false}
      placement='top'
      target={
        <UserContainer>
          {usernameField}
          {displayNameField}
        </UserContainer>
      }
    />
  ) : (
    <UserContainer>
      {usernameField}
      {displayNameField}
    </UserContainer>
  );

  return (
    <SourceWrapper className={className}>
      <UserDetailsWrapper
        isClickable={isClickable}
        target='_blank'
        {...(userLink ? { href: userLink } : {})}
      >
        {!isProduct && (
          <SmallAvatar
            imageUrl={networkUserImageUrl}
            initials={networkUserInitials}
            size={size}
            username={networkUsername}
          />
        )}
        {userContainer}
      </UserDetailsWrapper>
      {itemInfo && (
        <RightAlignedSourceButton
          ingestionSource={ingestionSource}
          isProduct={isProduct}
          isUploaded={isUploaded}
          itemInfo={itemInfo}
        />
      )}
    </SourceWrapper>
  );
};

const Flex = styled.div`
  display: flex;
`;

const SourceWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const UserDetailsWrapper = styled.a<{ isClickable: boolean }>(
  ({ isClickable }) => `
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  cursor: ${isClickable ? 'pointer' : 'default'};
  &:hover {
    text-decoration: none !important;
  }
`,
);

const UserContainer = styled(Flex)`
  flex-direction: column;
  flex: 0 1 auto;
  min-width: 0;
`;

const SmallAvatar = styled(Avatar)(
  ({ theme }) => `
  margin-right: ${theme.spacings[2]};
  flex: 0 0 auto;
`,
);

const MissingUsername = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
`,
);

const MissingUsernameText = styled(Text)(
  ({ theme }) => `
  font-style: italic;
`,
);

const RightAlignedSourceButton = styled(SourceActionButton)(
  ({ theme }) => `
  /*
   * Push the source button to the right so that the icon
   * itself is aligned with the edge of its parent component.
   * This allows the background that appears on hover to
   * overflow outside the component.
   */
  position: relative;
  left: ${theme.spacings[2]};
  flex: 0 0 auto;
`,
);

export default Source;
